import React, { useEffect, useRef, useState } from 'react';

import { Send, Mic } from '@material-ui/icons';
import { Grid, IconButton, InputAdornment, OutlinedInput } from '@mui/material';

import styles from './ChatNewMessage.module.css'
import ChatFooter from '../ChatFooter';
import AudioRecorder from '../AudioRecorder';
import FilePreview from '../FilePreview';

const ChatNewMessage = ({
  newMessage,
  handleNewMessageChange,
  handleSendMessage,
  handleSendingMessage,
  status,
  sendFile,
  setNewMessage,
  disableChat
}) => {
  const textAreaRef = useRef();
  const maxLength = 250;
  const [charactersLeft, setCharactersLeft] = useState(maxLength - newMessage.length);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showAudioRecorder, setShowAudioRecorder] = useState(false);
  const [startRecordingNow, setStartRecordingNow] = useState(false);
  const audioRecorderRef = useRef(null);

  const handleKeyPress = (e) => {
    if (e.shiftKey === false && e.key === 'Enter') {
      e.preventDefault();
      handleSendMessage();
      setCharactersLeft(maxLength);
    }
  };

  const handleChange = (event) => {
    handleNewMessageChange(event); // Chama o manipulador passado como prop para atualizar o estado da mensagem
    setCharactersLeft(maxLength - event.target.value.length); // Atualiza o contador de caracteres restantes
  };

  const handleSendFile = (file) => {
    setSelectedFile(file);
  };

  const handleSendMessageWithFile = () => {
    const isImageFile = selectedFile && (selectedFile.type || '').startsWith('image/');
    if (selectedFile) {
      // Para imagem, envia o texto do preview como LEGENDA junto do arquivo
      // (estilo WhatsApp), numa mensagem só — não como mensagem separada.
      sendFile(selectedFile, isImageFile ? newMessage : '');
      setSelectedFile(null);
      if (isImageFile && newMessage) {
        setNewMessage('');
      }
    }
    // Se houver áudio gravado ou gravando, enviar também
    if (audioRecorderRef.current && (audioRecorderRef.current.getCurrentAudio || audioRecorderRef.current.getAudioAfterStop)) {
      const current = audioRecorderRef.current.getCurrentAudio ? audioRecorderRef.current.getCurrentAudio() : null;
      const sendBase64 = (audioBase64) => {
        const base64Data = audioBase64.split(',')[1];
        const byteCharacters = atob(base64Data);
        const byteArrays = [];
        for (let i = 0; i < byteCharacters.length; i++) byteArrays.push(byteCharacters.charCodeAt(i));
        const byteArray = new Uint8Array(byteArrays);
        const blob = new Blob([byteArray], { type: 'audio/ogg; codecs=opus' });
        
        // Criar um File object com nome para o FormData aceitar corretamente
        const audioFile = new File([blob], `audio_${Date.now()}.ogg`, { type: 'audio/ogg; codecs=opus' });
        sendFile(audioFile);
        setShowAudioRecorder(false);
        setStartRecordingNow(false);
      };
      if (current) {
        sendBase64(current);
      } else if (audioRecorderRef.current.getAudioAfterStop) {
        audioRecorderRef.current.getAudioAfterStop().then(a => a && sendBase64(a));
      }
    }
    // Envia o texto como mensagem separada apenas quando NÃO é legenda de imagem
    // (na imagem o texto já foi enviado como legenda acima).
    if (newMessage && !isImageFile) {
      handleSendMessage();
    }
  };

  useEffect(() => {

    const textArea = textAreaRef.current;
    textArea.addEventListener('keypress', handleSendingMessage);

    return () => {
      textArea.removeEventListener('keypress', handleSendingMessage);
    };
  }, [handleSendMessage, handleSendingMessage]);

  return (
    <Grid container xs={12}>
      <Grid className={styles.characterCounter}>
        {charactersLeft} caracteres restantes
      </Grid>

      {/* FilePreview para mostrar preview do arquivo selecionado */}
      {selectedFile && (
        <FilePreview
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          handleSendMessage={handleSendMessageWithFile}
          newMessage={newMessage}
          setNewMessage={(value) => handleNewMessageChange({ target: { value } })}
          isChat={true}
        />
      )}

      {!showAudioRecorder && (
      <OutlinedInput
        id="text-area"
        inputRef={textAreaRef}
        value={newMessage}
        fullWidth
        multiline
        maxRows={4}
        onChange={handleChange}
        variant='outlined'
        className={styles.newMessageInputField}
        disabled={disableChat}
        onKeyPress={handleKeyPress}
        sx={{
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border-strong)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--accent)' },
          backgroundColor: 'var(--surface-elevated)',
        }}
        endAdornment={
          <InputAdornment position='end'>
            <IconButton aria-label="Send message" disabled={disableChat}>
              <Send onClick={() => {
                handleSendMessage();
                setCharactersLeft(maxLength);
              }} className="send-message-button" fontSize="large" />
            </IconButton>
            <IconButton
              aria-label="Record audio"
              disabled={disableChat}
              onClick={() => { setShowAudioRecorder(true); setStartRecordingNow(true); }}
              sx={{
                color: 'var(--text-secondary)',
                '&:hover': {
                  backgroundColor: 'var(--surface-muted)',
                },
                transition: 'background-color 0.15s ease',
              }}
            >
              <Mic />
            </IconButton>
            <IconButton aria-label="Send file" disabled={disableChat}>
              <ChatFooter status={status} sendFile={handleSendFile} />
            </IconButton>
          </InputAdornment>
        }
        inputProps={{ maxLength: maxLength }}
      />)}

      {showAudioRecorder && (
        <Grid 
          container 
          alignItems="center" 
          justifyContent="space-between" 
          spacing={1}
          sx={{
            padding: '12px',
            backgroundColor: 'var(--surface-elevated)',
            borderRadius: '12px',
            margin: '8px 14px',
            boxShadow: 'var(--shadow-sm)',
            transition: 'border-color 0.15s ease',
            border: '1px solid var(--border)',
          }}
        >
          <Grid item sx={{ flex: 1, minWidth: 0, marginRight: '16px' }}>
            <AudioRecorder
              ref={audioRecorderRef}
              autoStart={startRecordingNow}
              onSave={() => {}}
              onRecordingStart={() => { setStartRecordingNow(false); }}
              onRecordingEnd={() => {}}
              onCancel={() => { setShowAudioRecorder(false); setStartRecordingNow(false); }}
            />
          </Grid>
          <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1, marginLeft: '8px' }}>
            <IconButton
              aria-label="send-audio"
              disabled={disableChat}
              sx={{
                backgroundColor: 'var(--accent)',
                color: '#fafaf9',
                '&:hover': {
                  backgroundColor: 'var(--accent-hover)',
                },
                '&:disabled': {
                  backgroundColor: 'var(--border)',
                  color: 'var(--text-muted)',
                },
                transition: 'background-color 0.15s ease',
                boxShadow: 'none',
              }}
            >
              <Send onClick={handleSendMessageWithFile} fontSize="large" />
            </IconButton>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};


export default ChatNewMessage;